import React, { useState, useEffect } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, PrinterIcon, XMarkIcon } from '@heroicons/react/24/outline';

const AuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [filters, setFilters] = useState({
    documentNumber: '',
    operationType: '',
    startDate: '',
    endDate: '',
    username: ''
  });
  const [operationTypes, setOperationTypes] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const itemsPerPage = 20;

  useEffect(() => {
    fetchOperationTypes();
    fetchLogs();
  }, [currentPage, filters]);

  const fetchOperationTypes = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/audit/operation-types`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setOperationTypes(result.data);
      }
    } catch (error) {
      console.error('Error fetching operation types:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const queryParams = new URLSearchParams({
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/audit/all?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setLogs(result.data.logs);
        setTotalRecords(result.data.total);
        setTotalPages(Math.ceil(result.data.total / itemsPerPage));
      } else {
        console.error('Failed to fetch logs');
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogsByDocumentNumber = async (documentNumber) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/audit/document/${documentNumber}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        setLogs(result.data.logs);
        setTotalRecords(result.data.total);
        setTotalPages(1);
        setCurrentPage(1);
      }
    } catch (error) {
      console.error('Error fetching logs by document number:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      documentNumber: '',
      operationType: '',
      startDate: '',
      endDate: '',
      username: ''
    });
    setCurrentPage(1);
  };

  const handleSearch = () => {
    if (filters.documentNumber) {
      fetchLogsByDocumentNumber(filters.documentNumber);
    } else {
      fetchLogs();
    }
  };

  const printLogs = () => {
    window.print();
  };

  const showLogDetails = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedLog(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b" id="removeLayoutHeader">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 rtl:space-x-reverse">
              <img 
                src="/logo-ar-black.png" 
                alt="شعار وزارة الصحة" 
                className="h-12"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">
                  مبادرة السيد رئيس الجمهورية لفحص المقبلين على الزواج
                </h1>
                <p className="text-sm text-gray-500">سجل المعاملات والعمليات</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-8" dir="rtl">
        <div className="bg-white rounded-lg shadow">
          {/* Filters */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  رقم المستند
                </label>
                <input
                  type="text"
                  placeholder="2025-12345678"
                  value={filters.documentNumber}
                  onChange={(e) => handleFilterChange('documentNumber', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  نوع العملية
                </label>
                <select
                  value={filters.operationType}
                  onChange={(e) => handleFilterChange('operationType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">جميع العمليات</option>
                  {operationTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  من تاريخ
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  إلى تاريخ
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  اسم المستخدم
                </label>
                <input
                  type="text"
                  placeholder="اسم المستخدم"
                  value={filters.username}
                  onChange={(e) => handleFilterChange('username', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center justify-between mt-4">
              <div className="flex space-x-3 rtl:space-x-reverse">
                <button
                  onClick={handleSearch}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
                >
                  <MagnifyingGlassIcon className="w-4 h-4 ml-2" />
                  بحث
                </button>
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  <XMarkIcon className="w-4 h-4 ml-2" />
                  مسح الفلاتر
                </button>
              </div>

              <button
                onClick={printLogs}
                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                <PrinterIcon className="w-4 h-4 ml-2" />
                طباعة
              </button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="px-6 py-3 bg-gray-50 border-b">
            <p className="text-sm text-gray-600">
              إجمالي النتائج: {totalRecords} معاملة | الصفحة {currentPage} من {totalPages}
            </p>
          </div>

          {/* Logs Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    اسم المستخدم
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    تاريخ العملية
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    نوع العملية
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    رقم العينة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    باركود عينة الHVD
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الحالة القديمة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الحالة الجديدة
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center">
                      <div className="spinner mx-auto mb-2"></div>
                      <p className="text-gray-500">جاري تحميل البيانات...</p>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      لا توجد معاملات لعرضها
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <tr key={log.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.username}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {formatDate(log.operationDate)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          log.operationType === 'إضافة' ? 'bg-green-100 text-green-800' :
                          log.operationType === 'استلام نتيجة الHVD' ? 'bg-blue-100 text-blue-800' :
                          log.operationType === 'طباعة' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.operationType}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.sampleNumber || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                        {log.hvdBarcode || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {log.oldStatusAr || '-'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          log.newStatusAr === 'جاهز للطباعة' ? 'bg-green-100 text-green-800' :
                          log.newStatusAr === 'تم الطباعة' ? 'bg-purple-100 text-purple-800' :
                          log.newStatusAr === 'منتظر نتيجة الHVD' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.newStatusAr}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        <button
                          onClick={() => showLogDetails(log)}
                          className="text-blue-600 hover:text-blue-900 font-medium"
                        >
                          تفاصيل
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-3 bg-white border-t flex items-center justify-between">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
                
                <span className="text-sm text-gray-700">
                  الصفحة {currentPage} من {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-md border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                </button>
              </div>

              <div className="text-sm text-gray-500">
                عرض {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalRecords)} من {totalRecords}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal for Log Details */}
      {showModal && selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto" dir="rtl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">تفاصيل المعاملة</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
            
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">اسم المستخدم</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.username}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">تاريخ العملية</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDate(selectedLog.operationDate)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">نوع العملية</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.operationType}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">رقم المستند</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.documentNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">رقم العينة</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.sampleNumber || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">باركود عينة الHVD</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{selectedLog.hvdBarcode || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">الحالة القديمة</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.oldStatusAr || '-'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">الحالة الجديدة</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedLog.newStatusAr || '-'}</p>
                </div>
              </div>
              
              {selectedLog.prenupId && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="font-medium text-gray-900 mb-2">معلومات الاستمارة</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">الاسم</label>
                      <p className="mt-1 text-sm text-gray-900">
                        {selectedLog.prenupId.FIRSTNAME} {selectedLog.prenupId.SECONDNAME} {selectedLog.prenupId.THIRDNAME}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">الرقم القومي</label>
                      <p className="mt-1 text-sm text-gray-900 font-mono">{selectedLog.prenupId.NID}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                إغلاق
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogs;